import { useAuthHttp, useReactHookForm } from '@hooks';
import { LoginResponse } from '@views/Login/types';
import { AxiosError } from 'axios';
import { useCallback, useState } from 'react';
import { Stack, TextField } from '@mui/material';
import { Text } from '@components/Text';
import { ErrorMessage } from '@components/ErrorMessage';
import { Controller } from 'react-hook-form';
import { Button } from '@components/Button';
import { defaultValues, validationSchema } from './form';
import { LOGIN_WITH_TWO_FACTOR_AUTH_URL } from '../../constants';

interface LoginWith2FaFormProps {
  twoFactorId: string;
  handleLoginResponse: (loginResponse: LoginResponse) => void;
}

export type LoginWith2FaFormValues = {
  twoFaCode: string;
  rememberBrowser: boolean;
};

export function LoginWith2FaForm({
  twoFactorId,
  handleLoginResponse,
}: LoginWith2FaFormProps) {
  const authHttp = useAuthHttp();
  const [failureMessage, setFailureMessage] = useState('');

  const { handleSubmit, control, isSubmitting, trigger } =
    useReactHookForm<LoginWith2FaFormValues>({
      defaultValues,
      validationSchema,
    });

  const focusTextFieldCallbackRef = useCallback(
    (inputElement: HTMLDivElement) => {
      setTimeout(() => {
        inputElement?.querySelector('input')?.focus();
      }, 1);
    },
    []
  );

  const onSubmit = async (values: LoginWith2FaFormValues) => {
    setFailureMessage('');

    const isValid = await trigger();

    if (isValid) {
      try {
        const { status, data } = await authHttp.post(
          LOGIN_WITH_TWO_FACTOR_AUTH_URL,
          {
            twoFactorId,
            code: values.twoFaCode,
            rememberBrowser: values.rememberBrowser,
          }
        );

        if (status === 200) {
          setFailureMessage('');
          handleLoginResponse(data);
        } else {
          setFailureMessage('Something went wrong. Please try again');
        }
      } catch (error) {
        const status = (error as AxiosError).response?.status;
        if (status === 400) {
          setFailureMessage(
            "That code wasn't recognised. Check your authenticator app and try again"
          );
        } else if (status === 401) {
          setFailureMessage(
            'Your login attempt has expired. Close this window and sign in again'
          );
        } else {
          setFailureMessage('Something went wrong. Please try again');
        }
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={24} p={6}>
        <Text>To login, enter the code in your Authenticator App below.</Text>
        <Controller
          name='twoFaCode'
          control={control}
          render={({ field, fieldState: { error, isTouched } }) => (
            <TextField
              {...field}
              name={field.name}
              error={Boolean(error && isTouched)}
              helperText={Boolean(error && isTouched) && error?.message}
              placeholder='Authenticator App Code (required)'
              ref={focusTextFieldCallbackRef}
            />
          )}
        />
        <Controller
          name='rememberBrowser'
          control={control}
          render={({ field }) => (
            <div>
              <label className='flex items-center gap-2 cursor-pointer'>
                <input
                  type='checkbox'
                  checked={field.value}
                  onChange={(event) => field.onChange(event.target.checked)}
                  className='h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary'
                />
                <Text>Remember this browser for 30 days</Text>
              </label>
              <p className='ml-6 mt-1 text-xs text-gray-500'>
                Stays trusted even after you log out — untick on shared
                machines.
              </p>
            </div>
          )}
        />
        {failureMessage && <ErrorMessage>{failureMessage}</ErrorMessage>}
        <Button type='submit' size='sm'>
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </Button>
      </Stack>
    </form>
  );
}
